import React, { useRef, useState, useEffect } from 'react'; // Added useState, useEffect
import html2canvas from 'html2canvas';
import { motion } from "framer-motion";
import { useLocation, Link, useNavigate } from 'react-router-dom'; // Added useNavigate
// Import icons from react-icons
import { FaSave, FaHome, FaGamepad } from 'react-icons/fa';
import styles from '../assets/css/Receipt.module.css'; // Import NEW CSS module
import { toast } from 'react-toastify'; // For user feedback

function Receipt() {
  const receiptRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate(); // Hook for navigation

  // State for the received data with defaults
  const [receiptData, setReceiptData] = useState({
      gameName: 'N/A',
      playDate: 'N/A',
      betDetails: [], // Changed from 'numbers' to 'betDetails' for clarity
      totalBetAmount: 0,
      gameType: 'N/A'
  });
  const [isSaving, setIsSaving] = useState(false); // State for save button loading

  // Process received data on component mount
  useEffect(() => {
      const receivedState = location.state?.receiptData;
      console.log("Received state in /receipt:", receivedState);

      if (Array.isArray(receivedState) && receivedState.length >= 5) {
          setReceiptData({
              gameName: receivedState[0] || 'N/A',
              playDate: receivedState[1] || 'N/A',
              betDetails: Array.isArray(receivedState[2]) ? receivedState[2] : [], // Ensure it's an array
              totalBetAmount: receivedState[3] || 0,
              gameType: receivedState[4] || 'N/A'
          });
      } else {
          console.error("Invalid or missing receipt data in location state.");
          toast.error("Receipt data is missing or invalid.", { theme: "dark" });
          // Optional: Redirect back or show an error message component
          // navigate('/dashboard'); // Example redirect
      }
  }, [location.state, navigate]);


  const handleSaveReceipt = () => {
    if (!receiptRef.current) {
      console.error("Receipt element ref is not available.");
      toast.error("Could not find receipt element.", { theme: "dark" });
      return;
    }
    if (isSaving) return; // Prevent double clicks

    setIsSaving(true); // Set loading state
    toast.info("Generating receipt image...", { theme: "dark", autoClose: 2000 });

    // Options for html2canvas
    const options = {
        useCORS: true,
        // scale: window.devicePixelRatio || 1, // Capture at device resolution (can increase size)
        backgroundColor: '#ffffff', // Set explicit white background for the card capture
        logging: false, // Disable logging unless debugging
        scrollY: -window.scrollY
    };

    html2canvas(receiptRef.current, options).then(canvas => {
        const imageData = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = imageData;
        // Use a safe filename
        const filename = `receipt-${receiptData.gameName?.replace(/[^a-z0-9]/gi, '_') || 'game'}-${receiptData.playDate?.replace(/[^a-z0-9]/gi, '-') || 'date'}.png`;
        link.download = filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('Receipt image download initiated.');
        toast.success('Receipt saved successfully!', { theme: "dark" });
        setIsSaving(false); // Reset loading state

    }).catch(err => {
        console.error("Error generating receipt image:", err);
        toast.error("Error saving receipt image.", { theme: "dark" });
        setIsSaving(false); // Reset loading state
    });
  };

  // Go back to dashboard or previous relevant page
  const handleGoHome = () => {
      navigate('/dashboard'); // Navigate to dashboard
  };

  // Framer Motion variants (optional)
  const containerVariants = {
      hidden: { opacity: 0, scale: 0.95 },
      visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
  };

  return (
    // Use the Aghori theme container style
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
    <div className={styles.aghoriReceiptContainer}>
        {/* Attach the ref to the element you want to capture */}
        <div ref={receiptRef} className={styles.receiptCard}>
          {/* Header */}
          <div className={styles.header}>
            <h2 className={styles.title}><FaGamepad /> Receipt</h2>
            <button
                className={styles.actionButton}
                onClick={handleSaveReceipt}
                disabled={isSaving} // Disable while saving
            >
              <FaSave />
              <span>{isSaving ? 'Saving...' : 'Save'}</span>
            </button>
          </div>

          {/* Details Section */}
          <div className={styles.details}>
            <div className={styles.detailItem}>
              <strong className={styles.label}>Game Name :</strong>
              <span className={styles.value}>{receiptData.gameName}</span>
            </div>
            <div className={styles.detailItem}>
              <strong className={styles.label}>Game Type :</strong>
              <span className={styles.value}>{receiptData.gameType}</span>
            </div>
             <div className={styles.detailItem}>
              <strong className={styles.label}>Total Amount :</strong>
              <span className={`${styles.value} ${styles.highlightValue}`}>₹{receiptData.totalBetAmount}</span>
            </div>
            <div className={styles.detailItem}>
              <strong className={styles.label}>Play Date :</strong>
              <span className={styles.value}>{receiptData.playDate}</span>
            </div>
            {/* Optional: Add Transaction ID or other details if available */}
            {/* <div className={styles.detailItem}>
              <strong className={styles.label}>Transaction ID :</strong>
              <span className={styles.valueSmall}>{receiptData.transactionId || 'N/A'}</span>
            </div> */}
          </div>

          {/* Numbers Section */}
          {receiptData.betDetails.length > 0 && (
              <div className={styles.numbersSection}>
                 <h3 className={styles.numbersLabel}>Played Numbers</h3>
                <div className={styles.numbersBox}>
                  {receiptData.betDetails.map((item, index) => (
                    <span key={index} className={styles.numberItem}>
                      <span className={styles.numberValue}>{item.bet_numbers}</span>
                      <span className={styles.numberSeparator}>=</span>
                      <span className={styles.numberAmount}>{item.bet_money}</span>
                    </span>
                  ))}
                </div>
              </div>
          )}

          {/* Footer within the card */}
          <div className={styles.cardFooter}>
             {/* Use button for action */}
            <button className={styles.homeButton} onClick={handleGoHome}>
              <FaHome />
              <span>Dashboard</span>
            </button>
          </div>
           {/* End of receiptCard */}
        </div>
    </div>
      </motion.div>
  );
}

export default Receipt;
